/**
 * b2b-plus-save-draft.js
 *
 * Defines <b2b-plus-save-draft> — a cart-page button, rendered beside the
 * "Import Order Form" button, that saves the current order draft (the IndexedDB
 * cart) as a Shopify draft order and then clears the cart. There is NO pre-submit
 * validation: a draft save is not an order create, so whatever data is available
 * (line items, customer, company purchasing entity) is sent as-is.
 *
 * The save itself runs through `saveCartAsDraftOrder` in `b2b-plus/checkout-sdk`,
 * imported lazily on first click so the heavy checkout SDK only loads when the
 * buyer actually saves.
 *
 * Import map entry: "b2b-plus/save-draft"
 */

import { getCart, toastNotification, b2bPlusString } from 'b2b-plus/utils';

const str = (key, fallback) => b2bPlusString(key) || fallback;

/** True when the IndexedDB cart has at least one line with qty > 0. */
const cartHasOrderableItems = async () => {
  try {
    const cart = await getCart();
    return Object.values(cart ?? {}).flat().some((it) => Number(it.quantity) > 0);
  } catch {
    return false;
  }
};

class B2BPlusSaveDraft extends HTMLElement {
  /** @type {HTMLButtonElement|null} */ #btn = null;
  /** @type {HTMLElement|null} */       #label = null;
  #busy = false;
  /** @type {BroadcastChannel|null} */  #channel = null;

  connectedCallback() {
    if (this.dataset.ready) return;   // guard against re-entrant connects
    this.dataset.ready = '1';
    this.#render();
    this.#refreshEnabled();

    // Reflect cart changes (add / remove / clear) from this tab or others. The cart
    // layer posts `cart-updated` on the 'b2b-plus-cart' BroadcastChannel for every
    // write; `b2b:cart-saved` covers in-page saves that predate the channel.
    if (typeof BroadcastChannel === 'function') {
      try {
        this.#channel = new BroadcastChannel('b2b-plus-cart');
        this.#channel.addEventListener('message', this.#onCartChanged);
      } catch { this.#channel = null; }
    }
    document.addEventListener('b2b:cart-saved', this.#onCartChanged);
  }

  disconnectedCallback() {
    this.#channel?.removeEventListener('message', this.#onCartChanged);
    this.#channel?.close?.();
    this.#channel = null;
    document.removeEventListener('b2b:cart-saved', this.#onCartChanged);
  }

  #onCartChanged = () => { this.#refreshEnabled(); };

  /** Enable the button only when there is something to save (and not mid-save). */
  async #refreshEnabled() {
    if (this.#busy) return;
    const hasItems = await cartHasOrderableItems();
    if (this.#btn && !this.#busy) this.#btn.disabled = !hasItems;
  }

  #render() {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'b2b-plus-btn b2b-plus-btn-secondary b2b-plus-save-draft__btn';
    btn.setAttribute('data-save-draft-btn', '');
    btn.disabled = true;   // enabled by #refreshEnabled once a non-empty cart is confirmed
    btn.innerHTML = `
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
      </svg>
      <span data-save-draft-label></span>`;
    this.#label = btn.querySelector('[data-save-draft-label]');
    if (this.#label) this.#label.textContent = str('cartSaveDraftButton', 'Save Order as Draft');
    btn.addEventListener('click', this.#onClick);
    this.#btn = btn;
    this.appendChild(btn);
  }

  #onClick = async () => {
    if (this.#busy) return;
    this.#busy = true;
    const restore = this.#label?.textContent ?? str('cartSaveDraftButton', 'Save Order as Draft');
    if (this.#btn) this.#btn.disabled = true;
    if (this.#label) this.#label.textContent = str('checkoutSavingDraft', 'Saving order');

    try {
      const { saveCartAsDraftOrder } = await import('b2b-plus/checkout-sdk');
      await saveCartAsDraftOrder();
      toastNotification(str('checkoutSaveDraftSuccess', 'Order saved.'), 'success');
      // The draft is saved and the cart cleared. Navigate to the saved-orders page
      // (matching the checkout page's save-draft) after a short delay so the success
      // toast is visible. Buttons stay disabled through this window.
      const savedPath = window.b2bPlus?.config?.checkout?.ordersSavedPath ?? '/apps/b2b/orders?mode=saved';
      setTimeout(() => { window.location.href = savedPath; }, 1500);
    } catch (err) {
      console.error('[b2b-plus] save order as draft failed', err);
      const serverMsg = String(err?.message ?? '').trim();
      const fallback  = str('checkoutSaveDraftError', 'Failed to save draft.');
      const message   = serverMsg && serverMsg.length < 300 ? serverMsg : fallback;
      toastNotification(message, 'error', message === fallback ? 5000 : 6000);
      this.#busy = false;
      if (this.#label) this.#label.textContent = restore;
      this.#refreshEnabled();   // submit failed — re-derive disabled state for a retry
    }
  };
}

if (!customElements.get('b2b-plus-save-draft')) {
  customElements.define('b2b-plus-save-draft', B2BPlusSaveDraft);
}
